import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Search } from "lucide-react";
import { toast } from "sonner";
import api, { fmtApiError } from "@/lib/adminApi";
import { Badge, EmptyState, fmtMoney, inputCls, CURRENCIES, SUBJECT_OPTIONS, STUDENT_STATUSES } from "./shared";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export const emptyStudent = {
    student_name: "", parent_name: "", parent_email: "", phone: "", country: "", timezone: "",
    subjects: [], class_level: "", teacher_id: "", classes_per_month: "", class_duration: "60 min",
    monthly_fee: "", currency: "INR", enrollment_date: new Date().toISOString().slice(0, 10), status: "active", notes: "",
};

export const StudentDialog = ({ open, onClose, initial, teachers, onSaved }) => {
    const [form, setForm] = useState(emptyStudent);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (open) setForm(initial ? { ...emptyStudent, ...initial } : emptyStudent);
    }, [open, initial]);

    const set = (k) => (e) => setForm({ ...form, [k]: e.target ? e.target.value : e });
    const toggleSubject = (s) =>
        setForm({ ...form, subjects: form.subjects.includes(s) ? form.subjects.filter((x) => x !== s) : [...form.subjects, s] });

    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        const payload = {
            ...form,
            classes_per_month: form.classes_per_month === "" ? null : Number(form.classes_per_month),
            monthly_fee: form.monthly_fee === "" ? null : Number(form.monthly_fee),
            teacher_id: form.teacher_id || null,
        };
        try {
            if (initial?.id) await api.patch(`/students/${initial.id}`, payload);
            else await api.post("/students", payload);
            toast.success(initial?.id ? "Student updated" : "Student added");
            onSaved();
            onClose();
        } catch (err) {
            toast.error(fmtApiError(err));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl" data-testid="student-dialog">
                <DialogHeader>
                    <DialogTitle className="font-serif text-2xl">{initial?.id ? "Edit Student" : "Add Student"}</DialogTitle>
                </DialogHeader>
                <form onSubmit={submit} className="mt-2 space-y-4">
                    <div className="grid gap-4 sm:grid-cols-2">
                        <div className="space-y-1.5">
                            <Label>Student name *</Label>
                            <Input required value={form.student_name} onChange={set("student_name")} className={inputCls} data-testid="student-name-input" />
                        </div>
                        <div className="space-y-1.5">
                            <Label>Parent / guardian</Label>
                            <Input value={form.parent_name} onChange={set("parent_name")} className={inputCls} data-testid="student-parent-input" />
                        </div>
                        <div className="space-y-1.5">
                            <Label>Parent email</Label>
                            <Input type="email" value={form.parent_email} onChange={set("parent_email")} className={inputCls} data-testid="student-email-input" />
                        </div>
                        <div className="space-y-1.5">
                            <Label>WhatsApp / phone (with country code)</Label>
                            <Input value={form.phone} onChange={set("phone")} className={inputCls} placeholder="+1 555 123 4567" data-testid="student-phone-input" />
                        </div>
                        <div className="space-y-1.5">
                            <Label>Country</Label>
                            <Input value={form.country} onChange={set("country")} className={inputCls} placeholder="USA, Australia, Canada…" data-testid="student-country-input" />
                        </div>
                        <div className="space-y-1.5">
                            <Label>Time zone</Label>
                            <Input value={form.timezone} onChange={set("timezone")} className={inputCls} placeholder="EST, AEST, IST…" data-testid="student-timezone-input" />
                        </div>
                    </div>
                    <div className="space-y-1.5">
                        <Label>Subjects</Label>
                        <div className="flex flex-wrap gap-2" data-testid="student-subjects-group">
                            {SUBJECT_OPTIONS.map((s) => (
                                <button type="button" key={s} onClick={() => toggleSubject(s)} data-testid={`student-subject-${s.toLowerCase()}`}
                                    className={`rounded-full border px-4 py-1.5 text-sm font-medium transition-colors ${
                                        form.subjects.includes(s) ? "border-teal bg-teal text-cream" : "border-border bg-white text-ink/70 hover:border-teal"
                                    }`}>
                                    {s}
                                </button>
                            ))}
                        </div>
                    </div>
                    <div className="grid gap-4 sm:grid-cols-3">
                        <div className="space-y-1.5">
                            <Label>Class / standard</Label>
                            <Input value={form.class_level} onChange={set("class_level")} className={inputCls} placeholder="Grade 5" data-testid="student-class-input" />
                        </div>
                        <div className="space-y-1.5">
                            <Label>Assigned teacher</Label>
                            <Select value={form.teacher_id} onValueChange={set("teacher_id")}>
                                <SelectTrigger className={inputCls} data-testid="student-teacher-select"><SelectValue placeholder="Choose teacher" /></SelectTrigger>
                                <SelectContent>
                                    {teachers.map((t) => (
                                        <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-1.5">
                            <Label>Status</Label>
                            <Select value={form.status} onValueChange={set("status")}>
                                <SelectTrigger className={inputCls} data-testid="student-status-select"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {STUDENT_STATUSES.map((s) => (
                                        <SelectItem key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-1.5">
                            <Label>Classes per month</Label>
                            <Input type="number" min="0" value={form.classes_per_month} onChange={set("classes_per_month")} className={inputCls} data-testid="student-classes-per-month-input" />
                        </div>
                        <div className="space-y-1.5">
                            <Label>Class duration</Label>
                            <Input value={form.class_duration} onChange={set("class_duration")} className={inputCls} placeholder="60 min" />
                        </div>
                        <div className="space-y-1.5">
                            <Label>Enrollment date</Label>
                            <Input type="date" value={form.enrollment_date} onChange={set("enrollment_date")} className={inputCls} data-testid="student-enrollment-input" />
                        </div>
                        <div className="space-y-1.5">
                            <Label>Monthly fee</Label>
                            <Input type="number" min="0" step="0.01" value={form.monthly_fee} onChange={set("monthly_fee")} className={inputCls} placeholder="4000" data-testid="student-fee-input" />
                        </div>
                        <div className="space-y-1.5">
                            <Label>Currency</Label>
                            <Select value={form.currency} onValueChange={set("currency")}>
                                <SelectTrigger className={inputCls} data-testid="student-currency-select"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {CURRENCIES.map((c) => (
                                        <SelectItem key={c} value={c}>{c}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    <div className="space-y-1.5">
                        <Label>Notes</Label>
                        <Textarea rows={2} value={form.notes} onChange={set("notes")} className={inputCls} data-testid="student-notes-input" />
                    </div>
                    <div className="flex justify-end gap-3 pt-2">
                        <button type="button" onClick={onClose} className="rounded-full border border-border px-6 py-2.5 text-sm font-medium text-ink hover:bg-sand" data-testid="student-cancel-button">Cancel</button>
                        <button type="submit" disabled={saving} className="rounded-full bg-saffron px-6 py-2.5 text-sm font-medium text-cream hover:bg-teal disabled:opacity-60" data-testid="student-save-button">
                            {saving ? "Saving…" : initial?.id ? "Save Changes" : "Add Student"}
                        </button>
                    </div>
                </form>
            </DialogContent>
        </Dialog>
    );
};

export default function AdminStudents() {
    const [data, setData] = useState(null);
    const [q, setQ] = useState("");
    const [status, setStatus] = useState("");
    const [page, setPage] = useState(1);
    const [teachers, setTeachers] = useState([]);
    const [dialog, setDialog] = useState({ open: false, initial: null });

    const load = () =>
        api.get("/students", { params: { q, status, page } }).then((r) => setData(r.data)).catch(() => toast.error("Could not load students"));

    useEffect(() => { load(); }, [q, status, page]);
    useEffect(() => { api.get("/teachers").then((r) => setTeachers(r.data.items)); }, []);

    return (
        <div className="space-y-6" data-testid="admin-students">
            <div className="flex flex-wrap items-center justify-between gap-4">
                <div>
                    <h1 className="font-serif text-3xl font-semibold text-ink">Students</h1>
                    <p className="mt-1 text-sm text-muted-foreground">{data ? `${data.total} total` : "Loading…"}</p>
                </div>
                <button onClick={() => setDialog({ open: true, initial: null })} data-testid="add-student-button"
                    className="flex items-center gap-2 rounded-full bg-saffron px-6 py-2.5 text-sm font-medium text-cream transition-colors hover:bg-teal">
                    <Plus className="h-4 w-4" /> Add Student
                </button>
            </div>

            <div className="flex flex-wrap gap-3">
                <div className="relative flex-1 min-w-60">
                    <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} placeholder="Search by student, parent or email…"
                        className={`${inputCls} pl-11`} data-testid="students-search-input" />
                </div>
                <Select value={status || "all"} onValueChange={(v) => { setStatus(v === "all" ? "" : v); setPage(1); }}>
                    <SelectTrigger className={`${inputCls} w-44`} data-testid="students-status-filter"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All statuses</SelectItem>
                        {STUDENT_STATUSES.map((s) => (
                            <SelectItem key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>

            {!data || data.items.length === 0 ? (
                <EmptyState message="No students yet. Click “Add Student” to enrol your first student." />
            ) : (
                <div className="overflow-x-auto rounded-2xl border border-border bg-white card-shadow">
                    <table className="w-full text-sm" data-testid="students-table">
                        <thead>
                            <tr className="border-b border-border text-left text-xs uppercase tracking-[0.14em] text-muted-foreground">
                                <th className="px-5 py-4 font-medium">Student</th>
                                <th className="px-5 py-4 font-medium">Parent</th>
                                <th className="px-5 py-4 font-medium">Subjects</th>
                                <th className="px-5 py-4 font-medium">Teacher</th>
                                <th className="px-5 py-4 font-medium">Monthly Fee</th>
                                <th className="px-5 py-4 font-medium">Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.items.map((s) => (
                                <tr key={s.id} className="border-b border-border/60 last:border-0 hover:bg-sand/50" data-testid={`student-row-${s.id}`}>
                                    <td className="px-5 py-4">
                                        <Link to={`/admin/students/${s.id}`} className="font-medium text-ink hover:text-saffron" data-testid={`student-link-${s.id}`}>
                                            {s.student_name}
                                        </Link>
                                        <p className="text-xs text-muted-foreground">{s.class_level || "—"}</p>
                                    </td>
                                    <td className="px-5 py-4">
                                        <p>{s.parent_name || "—"}</p>
                                        <p className="text-xs text-muted-foreground">{s.country || ""}</p>
                                    </td>
                                    <td className="px-5 py-4">{(s.subjects || []).join(", ") || "—"}</td>
                                    <td className="px-5 py-4">{s.teacher_name || <span className="text-muted-foreground">Unassigned</span>}</td>
                                    <td className="px-5 py-4 font-medium">{s.monthly_fee ? fmtMoney(s.monthly_fee, s.currency) : "—"}</td>
                                    <td className="px-5 py-4"><Badge value={s.status} /></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {data && data.pages > 1 && (
                <div className="flex items-center justify-center gap-3" data-testid="students-pagination">
                    <button disabled={page <= 1} onClick={() => setPage(page - 1)} className="rounded-full border border-border px-5 py-2 text-sm disabled:opacity-40" data-testid="students-prev-page">Previous</button>
                    <span className="text-sm text-muted-foreground">Page {data.page} of {data.pages}</span>
                    <button disabled={page >= data.pages} onClick={() => setPage(page + 1)} className="rounded-full border border-border px-5 py-2 text-sm disabled:opacity-40" data-testid="students-next-page">Next</button>
                </div>
            )}

            <StudentDialog open={dialog.open} initial={dialog.initial} teachers={teachers} onClose={() => setDialog({ open: false, initial: null })} onSaved={load} />
        </div>
    );
}
